// =====================================================
// useInstallSignal  (Refer & Earn — CONDITION 1)
//
// Reports to the backend that TheBlogsSphere is installed on this device.
// Mounted once, app-wide, alongside the existing activity tracker.
//
// WHAT IT DOES NOT DO — and this is the point:
//   • It does NOT change the Install button in any way. InstallButton.js owns
//     the beforeinstallprompt / appinstalled prompt and its own visibility rule
//     (hidden when the app is already installed). This hook only LISTENS to the
//     signals the browser is already emitting.
//   • It does NOT decide anything. It reports which signal it saw; the server
//     validates it, stamps its own clock and owns the ✅.
//   • It does NOT assume a fixed install duration. There is no 15–20 second
//     timer here: whether an install takes two seconds or two minutes depends on
//     the device, the network and the store, so the report fires when a real
//     signal arrives — whenever that is.
//
// THREE SIGNALS, IN ORDER OF STRENGTH
//   1. `appinstalled`  — the browser fired the install-completed event. This is
//                        the moment an install actually finished.
//   2. standalone      — the app is RUNNING as an installed app (display-mode:
//                        standalone, or navigator.standalone on iOS, which has
//                        no appinstalled event at all). Reported on every launch.
//   3. related-app     — getInstalledRelatedApps() confirms it is installed even
//                        though we are currently in a browser tab.
//
// SIGNALS SEEN BEFORE THERE IS AN ACCOUNT
// The Install button lives on the LOGIN page, so a referred visitor installs
// the app BEFORE they have a session — and /api/referral/install-signal needs
// one. A signal observed while logged out is therefore parked in localStorage
// and replayed on the first route change after a session exists (i.e. straight
// after login or registration). Nothing is invented: only a real browser signal
// is ever parked, the server still validates it, stamps its own clock and
// decides whether it counts. A signal that fails to deliver stays parked and is
// retried on the next navigation, and anything older than the referral window
// is dropped rather than credited late.
//
// Every report is de-duplicated per session, so an app opened in standalone
// mode does not post on every render.
// =====================================================

import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isAuthenticated } from "../utils/session";
import { sendInstallSignal } from "../services/referralApi";

// The signals the server accepts. Kept here too so a corrupted or stale parked
// entry can never reach the API.
const ALLOWED_SOURCES = ["appinstalled", "standalone", "related-app"];

// Where a logged-out signal waits for a session.
const PENDING_KEY = "pendingInstallSignals";

// The referral window is 24 hours, so a parked signal older than that could
// only ever be credited too late. It is dropped instead of replayed.
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

// True when the page is RUNNING as an installed app.
function isRunningInstalled() {
  try {
    return (
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || // Android/desktop PWA
      window.navigator.standalone === true // iOS home-screen app
    );
  } catch {
    return false;
  }
}

// Storage is best-effort throughout: private modes and full quotas must not
// break the install flow, so every read falls back to "nothing parked".
function readPending() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - PENDING_TTL_MS;
    return parsed.filter(
      (e) => e && ALLOWED_SOURCES.includes(e.source) && Number(e.at) > cutoff
    );
  } catch {
    return [];
  }
}

function writePending(list) {
  try {
    if (!list.length) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch {
    /* nothing to do — the signal fires again on the next launch */
  }
}

// Park a signal until a session exists. One entry per source: re-observing the
// same signal must not queue a second report.
function parkPending(source) {
  const list = readPending();
  if (list.some((e) => e.source === source)) return;
  list.push({ source, at: Date.now() });
  writePending(list);
}

function dropPending(source) {
  writePending(readPending().filter((e) => e.source !== source));
}

export default function useInstallSignal() {
  // <InstallSignal/> is mounted inside the router and never unmounts, so a
  // route change is the moment to look again: it is what happens immediately
  // after a successful login or registration.
  const { pathname } = useLocation();

  // Survives re-renders so a signal is posted at most once per page session.
  const reported = useRef(new Set());

  const deliver = useCallback((source) => {
    if (reported.current.has(source)) return;
    reported.current.add(source);
    // Fire-and-forget: the API layer swallows failures. A failed delivery is
    // parked again so the next navigation retries it, instead of the install
    // being silently lost.
    sendInstallSignal(source).then((data) => {
      if (data) {
        dropPending(source);
      } else {
        reported.current.delete(source);
        parkPending(source);
      }
    });
  }, []);

  const report = useCallback(
    (source) => {
      // No session yet — this is the referred visitor who installed from the
      // Login page. Keep the signal; it is replayed once they are in.
      if (!isAuthenticated()) {
        parkPending(source);
        return;
      }
      deliver(source);
    },
    [deliver]
  );

  useEffect(() => {
    // ── SIGNAL 1: the install just completed ──
    const onAppInstalled = () => report("appinstalled");
    window.addEventListener("appinstalled", onAppInstalled);

    // ── SIGNAL 2: we are running AS the installed app ──
    if (isRunningInstalled()) report("standalone");

    // The display mode can change without a reload (a tab promoted to an
    // installed window), so the media query is watched too.
    let mql = null;
    const onDisplayModeChange = (e) => {
      if (e.matches) report("standalone");
    };
    try {
      mql = window.matchMedia("(display-mode: standalone)");
      if (mql.addEventListener) mql.addEventListener("change", onDisplayModeChange);
      else if (mql.addListener) mql.addListener(onDisplayModeChange); // older Safari
    } catch {
      mql = null;
    }

    // ── SIGNAL 3: installed, but we are in a browser tab right now ──
    // Chrome-only and requires a matching `related_applications` manifest
    // entry, so it is treated as a bonus: absent or rejected changes nothing.
    if (navigator.getInstalledRelatedApps) {
      navigator
        .getInstalledRelatedApps()
        .then((apps) => {
          if (apps && apps.length) report("related-app");
        })
        .catch(() => {});
    }

    return () => {
      window.removeEventListener("appinstalled", onAppInstalled);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener("change", onDisplayModeChange);
        else if (mql.removeListener) mql.removeListener(onDisplayModeChange);
      }
    };
  }, [report]);

  // ── REPLAY: anything observed before the session existed ──
  // Runs on mount and on every route change. It is a no-op with no session and
  // a no-op with nothing parked, which is the case for almost every navigation.
  useEffect(() => {
    if (!isAuthenticated()) return;
    readPending().forEach((entry) => deliver(entry.source));
  }, [pathname, deliver]);
}
